import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { getDb } from "@/lib/firebase-server";
import { type AuthorProfile, type Article } from "./types";
import { docToArticle } from "./articles";

const AUTHORS_COLLECTION = 'authors';
const ARTICLES_COLLECTION = 'articles';

/**
 * Returns the official, canonical name for an author.
 * Priority: Official Profile Name > Article Byline > Default.
 * Ensures Google News entity consistency across all surfaces.
 */
export function getCanonicalAuthorName(
  article: Article,
  authorProfile?: AuthorProfile | null
): string {
  const profileName = authorProfile?.name?.trim();
  const articleByline = article?.author?.trim();
  
  if (profileName) return profileName;
  if (articleByline && articleByline !== 'News Afrika Newsroom') return articleByline;
  
  return "News Afrika";
}

export function docToAuthor(doc: any): AuthorProfile {
    const data = doc.data();
    return {
        id: doc.id,
        name: data.name || 'Anonymous Journalist',
        profileSlug: data.profileSlug || doc.id || 'newsroom',
        bio: data.bio || '',
        image: data.image || data.images || null,
        areasOfExpertise: Array.isArray(data.areasOfExpertise) ? data.areasOfExpertise : [],
        socialLinks: Array.isArray(data.socialLinks) ? data.socialLinks : [],
    } as AuthorProfile;
}

export async function getAllAuthors(): Promise<AuthorProfile[]> {
    const db = getDb();
    if (!db) {
      return [];
    }
    try {
      const authorsSnapshot = await getDocs(collection(db, AUTHORS_COLLECTION));
      if (authorsSnapshot.empty) {
          return [];
      }
      return authorsSnapshot.docs
        .map(doc => docToAuthor(doc))
        .filter(author => !!author.profileSlug); // Filter out potential malformed data
    } catch (e) {
      return [];
    }
}

export async function getAuthorBySlug(slug: string): Promise<AuthorProfile | null> {
    const db = getDb();
    if (!db || !slug) {
        return null;
    }
    
    try {
      const q = query(
          collection(db, AUTHORS_COLLECTION),
          where("profileSlug", "==", slug)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
          // Fallback to check by ID if slug fails
          const docRef = doc(db, AUTHORS_COLLECTION, slug);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
              return docToAuthor(docSnap);
          }
          return null;
      }
      
      return docToAuthor(snapshot.docs[0]);
    } catch (e) {
      return null;
    }
}

export async function getArticlesByAuthor(authorId: string): Promise<Article[]> {
    const db = getDb();
    if (!db || !authorId) {
        return [];
    }

    try {
      const qById = query(
          collection(db, ARTICLES_COLLECTION),
          where("authorId", "==", authorId)
      );
      
      const qBySlug = query(
          collection(db, ARTICLES_COLLECTION),
          where("authorProfileSlug", "==", authorId)
      );

      const [snapshotById, snapshotBySlug] = await Promise.all([
        getDocs(qById),
        getDocs(qBySlug)
      ]);

      if (snapshotById.empty && snapshotBySlug.empty) {
          return [];
      }
      
      const articlesMap = new Map<string, Article>();

      snapshotById.docs.forEach(doc => {
          if (!articlesMap.has(doc.id)) {
              articlesMap.set(doc.id, docToArticle(doc.data()));
          }
      });

      snapshotBySlug.docs.forEach(doc => {
          if (!articlesMap.has(doc.id)) {
              articlesMap.set(doc.id, docToArticle(doc.data()));
          }
      });
      
      return Array.from(articlesMap.values()).sort((a, b) => 
        new Date(b.publishDate || 0).getTime() - new Date(a.publishDate || 0).getTime()
      );
    } catch (e) {
      return [];
    }
}
